import React, { useState } from 'react';
import { Download, Share2, FileText, BarChart3, Zap, AlertTriangle, CheckCircle, AlertCircle, Shield, Edit3, Info, Hammer, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ErrorsDetected } from './ErrorsDetected';
import { RulesBreakdown } from './RulesBreakdown';
import { Recommendations } from './Recommendations';
import { EvolutionCoefficientElectrique } from './EvolutionCoefficientElectrique';
import { WorkScenariosCallToAction } from './WorkScenariosCallToAction';
import { InconsistencyDetailCard } from './InconsistencyDetailCard';
import { CorrectiveActionCard } from './CorrectiveActionCard';
import { RecommendedWorks } from './RecommendedWorks';
import { CircularProgress } from '@/components/ui/Progress';
import { PDFDownloadButton } from '@/components/pdf/PDFDownloadButton';
import { analytics } from '@/utils/analytics';
import { cn } from '@/utils/cn';
import { calculateDPEProjection, calculateEtiquetteDPE, isEnergyClassBetter } from '@/utils/dpe-projections';
import { ExternalLinkTracker } from '@/components/analytics/ExternalLinkTracker';
import { useNavigate } from 'react-router-dom';

const handleShare = async (dpeId: string, results: any) => {
  try {
    const shareData = {
      title: 'Analyse DPE - DPEWizard',
      text: `Découvrez les résultats de mon analyse DPE : Classe ${results.data?.classe || 'D'}, ${results.data?.consommation || 0} kWh/m²/an`,
      url: `${window.location.origin}/shared/dpe/${dpeId}`
    };

    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(shareData.url);
      alert('Lien copié dans le presse-papier');
    }

    analytics.trackEvent({
      category: 'analysis',
      action: 'share',
      label: dpeId
    });
  } catch (error) {
    console.error('Share error:', error);
    alert('Erreur lors du partage');
  }
};

interface AnalysisResultsProps {
  dpeId: string;
  analysisId: string;
  results: any;
  onExportPDF?: () => void;
  onShare?: () => void;
  onModifyAnswers?: () => void;
  isLegacyDPE?: boolean;
}

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  dpeId,
  analysisId,
  results,
  onExportPDF,
  onShare,
  onModifyAnswers,
  isLegacyDPE = false
}) => {
  const navigate = useNavigate();
  // ✅ AJOUT DEBUG : Logguer la structure des données reçues
  React.useEffect(() => {
    console.log('🔍 DEBUG AnalysisResults - Structure complète de results:', {
      resultsKeys: Object.keys(results || {}),
      results: results,
      finalRecommendations: results?.final_recommendations,
      finalRecommendationsKeys: results?.final_recommendations ? Object.keys(results.final_recommendations) : 'N/A',
      sessionData: results?.sessionData,
      sessionDataKeys: results?.sessionData ? Object.keys(results.sessionData) : 'N/A'
    });
  }, [results]);
  
  React.useEffect(() => {
    analytics.trackEvent({
      category: 'analysis',
      action: 'view_results',
      label: dpeId,
      analysisId: analysisId
    });
  }, [dpeId, analysisId]);
  
  // ✅ CORRECTION : Parser les données avec gestion des strings JSON
      const parseJSONField = (field: any) => {
        if (!field) return null;
        if (typeof field === 'string') {
          try {
            return JSON.parse(field);
          } catch (e) {
            console.error('Failed to parse JSON field:', e);
            return null;
          }
        }
        return field;
      };
      
      const {
        factorsAnalysis: rawFactorsAnalysis = {},
        errorsDetected: rawErrorsDetected = [],
        opportunities: rawOpportunities = [],
        sessionData = {},
        userAnswers = [],
        confidenceScore: rawConfidenceScore,
        resultsConfidenceScore: rawResultsConfidenceScore,
        analysisDate = new Date().toISOString(),
        rulesApplied: rawRulesApplied = []
      } = results || {};

      // ✅ Parser les champs qui peuvent être des strings JSON
      const factorsAnalysis = parseJSONField(rawFactorsAnalysis) || {};
      const errorsDetected = parseJSONField(rawErrorsDetected) || [];
      const opportunities = parseJSONField(rawOpportunities) || [];
      const rulesApplied = parseJSONField(rawRulesApplied) || [];

      // ✅ CORRECTION : Parser le score de confiance avec nullish coalescing et multiples sources
      const scoreFromResults = rawConfidenceScore ?? rawResultsConfidenceScore;
      const resultsConfidenceScore = typeof scoreFromResults === 'string'
        ? parseFloat(scoreFromResults)
        : (scoreFromResults ?? 85);

      console.log('🔍 DEBUG AnalysisResults - Données parsées:', {
        confidenceScore: resultsConfidenceScore,
        confidenceScoreType: typeof resultsConfidenceScore,
        rawConfidenceScore,
        rawResultsConfidenceScore,
        errorsCount: errorsDetected.length,
        opportunitiesCount: opportunities.length,
        rulesCount: rulesApplied.length
      });

  // ✅ FIX: Mémoriser l'extraction des recommandations pour éviter la boucle infinie
  const finalRecommendations = React.useMemo((): any => {
    let recommendations: any = {};

    // Approche 1: Vérifier si results lui-même contient les données JSON
    if (typeof results === 'string') {
      try {
        recommendations = JSON.parse(results);
        console.log('🔍 Approach 1: Parsed results as string worked');
        return recommendations;
      } catch (e) {
        console.log('🔍 Approach 1: Failed to parse results as string');
      }
    }

    // Approche 2: Vérifier si results.final_recommendations existe
    if (results?.final_recommendations) {
      recommendations = results.final_recommendations;
      console.log('🔍 Approach 2: Found in results.final_recommendations');

      // Forcer le parsing si c'est une chaîne
      if (typeof recommendations === 'string') {
        try {
          recommendations = JSON.parse(recommendations);
        } catch (e) {
          console.error('Error parsing final_recommendations:', e);
          recommendations = {};
        }
      }

      if (recommendations && typeof recommendations === 'object' && (recommendations as any).immediate_actions) {
        return recommendations;
      }
    }

    // Approche 3: Vérifier dans sessionData.final_recommendations_json
    if (results?.sessionData?.final_recommendations_json) {
      try {
        recommendations = typeof results.sessionData.final_recommendations_json === 'string'
          ? JSON.parse(results.sessionData.final_recommendations_json)
          : results.sessionData.final_recommendations_json;
        console.log('🔍 Approach 3: Found in sessionData.final_recommendations_json');
        return recommendations;
      } catch (e) {
        console.error('🔍 Approach 3: Error parsing sessionData.final_recommendations_json:', e);
      }
    }

    console.log('🔍 No recommendations found, returning empty object');
    return {};
  }, [results]);

  // ✅ FIX: Mémoriser les tableaux de recommandations
  const {
    immediate_actions = [],
    short_term_actions = [],
    medium_term_actions = [],
    long_term_actions = [],
    inconsistencies_detected = [],
    evolution_coefficient_electrique = null,
    estimated_total_cost = 0,
    estimated_total_savings = 0,
    confidence_score: finalRecommendationsScore
  } = finalRecommendations;

  // ✅ CORRECTION : Utiliser le score de finalRecommendations en priorité
  const confidence_score = finalRecommendationsScore ?? resultsConfidenceScore;

    // ✅ CORRECTION : Calculer projection coefficient électrique
  const dpeProjection = evolution_coefficient_electrique?.projection_2026 ||
                       calculateDPEProjection(sessionData?.dpe_data);

  // ✅ NOUVEAU : Déterminer si le nouveau système de calcul thermique est disponible
  // Si les recommandations sont basées sur des données thermiques réelles (pas des estimations),
  // on masque l'ancien système de projection basé uniquement sur le coefficient électrique
  const hasRealThermalCalculations = React.useMemo(() => {
    // Vérifier si les données DPE contiennent des informations thermiques détaillées
    const dpeData = sessionData?.dpe_data;
    if (!dpeData) return false;

    // Si on a des déperditions réelles ET des consommations finales par poste,
    // alors le système de calcul thermique est utilisable
    const hasDetailedHeatLoss = (
      dpeData.deperditions_murs !== null ||
      dpeData.deperditions_baies_vitrees !== null ||
      dpeData.deperditions_planchers_hauts !== null
    );

    const hasDetailedConsumption = (
      dpeData.conso_chauffage_ef !== null ||
      dpeData.conso_ecs_ef !== null
    );

    // Si on a les deux, on considère que le système thermique est actif
    return hasDetailedHeatLoss && hasDetailedConsumption;
  }, [sessionData?.dpe_data]);

  // Ne montrer l'ancien système que si le nouveau n'est pas disponible
  const showOldProjectionSystem = !hasRealThermalCalculations && dpeProjection;

  // ✅ FIX: Mémoriser toutes les recommandations pour éviter la boucle infinie
  const displayRecommendations = React.useMemo(() => {
    return [
      ...immediate_actions,
      ...short_term_actions,
      ...medium_term_actions,
      ...long_term_actions
    ];
  }, [immediate_actions, short_term_actions, medium_term_actions, long_term_actions]);

  // ✅ FIX: Mémoriser les corrections DPE
  const dpeCorrections = React.useMemo(() =>
    displayRecommendations.filter(rec =>
      rec.category === 'DPE_CORRECTION' || rec.category === 'DPE_OPTIMIZATION'
    ),
    [displayRecommendations]
  );

  // ✅ CORRECTION: Distinguer les vraies erreurs (CRITIQUE/MAJEURE) des simples préconisations (INFORMATION)
  const realInconsistencies = React.useMemo(() =>
    inconsistencies_detected.filter(inc => inc.severity !== 'INFORMATION'),
    [inconsistencies_detected]
  );

  // ✅ CORRECTION: Ne créer la section correction que s'il y a de vraies erreurs (pas juste des préconisations)
  const uniqueDpeCorrection = React.useMemo(() => {
    // Ne créer la recommandation groupée que s'il y a des incohérences non-INFORMATION
    if (realInconsistencies.length === 0) {
      console.log('🔍 No real inconsistencies (only INFORMATION) - Not creating correction section');
      return [];
    }

    return [{
      id: 'dpe_correction_grouped',
      title: 'Correction du DPE',
      description: `${realInconsistencies.length} incohérence${realInconsistencies.length > 1 ? 's' : ''} détectée${realInconsistencies.length > 1 ? 's' : ''} nécessitant une correction du diagnostic`,
      category: 'DPE_CORRECTION',
      estimated_cost: 250,
      priority: 'CRITIQUE',
      difficulty: 'FACILE',
      inconsistencies_count: realInconsistencies.length
    }];
  }, [realInconsistencies]);

  // ✅ NOUVEAU: Extraire le warning des systèmes collectifs (doit être calculé avant workRecommendations)
  const collectiveSystemWarning = React.useMemo(() =>
    displayRecommendations.find(rec => rec.category === 'SYSTEME_COLLECTIF'),
    [displayRecommendations]
  );

  // ✅ FIX: Mémoriser les recommandations de travaux avec filtrage intelligent des systèmes collectifs
  const workRecommendations = React.useMemo(() => {
    const filtered = displayRecommendations.filter(rec => {
      // Exclure les catégories non-travaux
      if (rec.category === 'DPE_CORRECTION' ||
          rec.category === 'DPE_OPTIMIZATION' ||
          rec.category === 'SYSTEME_COLLECTIF') {
        return false;
      }

      // Si chauffage collectif, exclure les recommandations de chauffage
      if (collectiveSystemWarning?.has_chauffage_collectif && rec.category === 'CHAUFFAGE') {
        console.log('🔍 Filtering out heating recommendation due to collective system:', rec.id);
        return false;
      }

      // Si ECS collectif, exclure les recommandations ECS (category pourrait être CHAUFFAGE pour ECS)
      if (collectiveSystemWarning?.has_ecs_collectif &&
          (rec.id?.includes('ecs') || rec.id?.includes('ballon'))) {
        console.log('🔍 Filtering out ECS recommendation due to collective system:', rec.id);
        return false;
      }

      return true;
    });

    console.log('🔍 Work recommendations after collective filtering:', {
      total: displayRecommendations.length,
      filtered: filtered.length,
      has_chauffage_collectif: collectiveSystemWarning?.has_chauffage_collectif,
      has_ecs_collectif: collectiveSystemWarning?.has_ecs_collectif
    });

    return filtered;
  }, [displayRecommendations, collectiveSystemWarning]);

  // ✅ CORRECTION : États de simulation après workRecommendations
  const [selectedWorkIds, setSelectedWorkIds] = useState<Set<string>>(new Set());
  const [simulatedConsumption, setSimulatedConsumption] = useState(0);
  const [simulatedEnergyClass, setSimulatedEnergyClass] = useState('');



  // ✅ CORRECTION : Extraire la surface habitable pour appliquer les seuils adaptatifs (réforme juillet 2024)
  const surfaceHabitable = React.useMemo(() => {
    return parseFloat(sessionData?.dpe_data?.living_area) ||
           parseFloat(sessionData?.dpe_data?.surface_habitable_logement) ||
           null;
  }, [sessionData?.dpe_data]);

  // ✅ CORRECTION : Calculer initialEnergyClass AVANT les hooks qui en dépendent
  const initialEnergyClass = results.data?.classe ||
                            results.data?.energy_class ||
                            sessionData?.dpe_data?.energy_class ||
                            'D';

  // ✅ FIX: Mémoriser les données DPE initiales avec calcul différencié 2026
   const initialDPEData = React.useMemo(() => {
    const rawConsommation = results.data?.consommation ||
                            results.data?.primary_energy_consumption ||
                            sessionData?.dpe_data?.conso_5_usages_par_m2_ep ||
                            sessionData?.dpe_data?.primary_energy_consumption ||
                            0;
  
    const dpeData = sessionData?.dpe_data;
    const dateEtablissement = dpeData?.date_etablissement_dpe || dpeData?.date_visite_diagnostiqueur;
    const isPre2026 = dateEtablissement && new Date(dateEtablissement) < new Date('2026-01-01'); // ✅ CORRECTION
  
    if (isPre2026 && rawConsommation > 0) { // ✅ Maintenant ça marche
      try {
        const projection = calculateDPEProjection(dpeData);
        console.log('🔧 Simulateur travaux - Projection 2026:', {
          raw: rawConsommation,
          projected: projection?.consoPar_m2_2026,
          isMixed: projection?.isMixedInstallation
        });
  
        if (projection && projection.consoPar_m2_2026 > 0) {
          return { consommation: Math.round(projection.consoPar_m2_2026) };
        }
      } catch (error) {
        console.error('Error calculating projection for simulator:', error);
      }
    }
  
    return { consommation: rawConsommation };
  }, [results.data, sessionData?.dpe_data]);

  // ✅ CORRECTION : Fonction de calcul de simulation DPE
  const calculateSimulatedDPE = React.useCallback(() => {
    const initialConsumption = initialDPEData.consommation;

    // ✅ CORRECTION : Partir de la consommation ACTUELLE (473), pas de la projection 2026
    // La projection 2026 est affichée séparément et ne doit pas être dans le simulateur
    let currentSimulatedConsumption = initialConsumption;

    // Appliquer les économies des travaux sélectionnés
    workRecommendations.forEach(rec => {
      if (selectedWorkIds.has(rec.id) && rec.estimated_savings) {
        currentSimulatedConsumption = Math.max(0, currentSimulatedConsumption - rec.estimated_savings);
      }
    });

    // ✅ CORRECTION : Passer la surface habitable pour appliquer les seuils adaptatifs (réforme juillet 2024)
    // Pour les logements < 40m², les seuils sont ajustés (ex: 15m² → F jusqu'à 513 kWh/m²/an au lieu de 420)
    const newClass = calculateEtiquetteDPE(currentSimulatedConsumption, surfaceHabitable || undefined);

    setSimulatedConsumption(currentSimulatedConsumption);
    setSimulatedEnergyClass(newClass);
  }, [selectedWorkIds, workRecommendations, initialDPEData.consommation, surfaceHabitable]);

  React.useEffect(() => {
    calculateSimulatedDPE();
  }, [calculateSimulatedDPE]);

  const handleToggleWork = (workId: string) => {
    setSelectedWorkIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workId)) {
        newSet.delete(workId);
      } else {
        newSet.add(workId);
      }
      return newSet;
    });
    
    analytics.trackEvent({
      category: 'simulation',
      action: selectedWorkIds.has(workId) ? 'unselect_work' : 'select_work',
      label: workId,
      custom_parameters: {
        dpe_id: dpeId,
        total_selected: selectedWorkIds.size
      }
    });
  };

  const totalSelectedSavings = React.useMemo(() => {
    // ✅ CORRECTION : Calculer uniquement les économies des travaux volontaires
    // L'évolution du coefficient électrique n'est plus dans le simulateur
    const totalSavings = workRecommendations
      .filter(rec => selectedWorkIds.has(rec.id))
      .reduce((total, rec) => total + (rec.estimated_savings || 0), 0);

    return totalSavings;
  }, [selectedWorkIds, workRecommendations]);

  const hasClassImprovement = simulatedEnergyClass && initialEnergyClass && 
                              isEnergyClassBetter(simulatedEnergyClass, initialEnergyClass);
  
  const formattedDate = new Date(analysisDate).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // ✅ AJOUT EFFECT : Gérer l'évolution du coefficient électrique
  // Supprimé car maintenant géré dans l'effect précédent

  const getReliabilityStatus = () => {
    // Séparer les vraies incohérences des préconisations d'optimisation
    const criticalInconsistencies = inconsistencies_detected.filter(inc => inc.severity === 'CRITIQUE').length;
    const majorInconsistencies = inconsistencies_detected.filter(inc => inc.severity === 'MAJEURE').length;

    // Les incohérences de type INFORMATION sont des préconisations, pas des erreurs DPE
    const optimizationSuggestions = inconsistencies_detected.filter(inc => inc.severity === 'INFORMATION').length;

    if (criticalInconsistencies > 0) {
      return {
        status: 'CRITIQUE',
        icon: <AlertCircle className="w-6 h-6 text-red-500" />,
        color: 'bg-red-50 border-red-200',
        textColor: 'text-red-800',
        title: 'DPE non fiable',
        description: 'Des erreurs critiques ont été détectées dans le diagnostic',
        action: 'Re-diagnostic recommandé'
      };
    } else if (majorInconsistencies > 0) {
      return {
        status: 'SUSPECT',
        icon: <AlertTriangle className="w-6 h-6 text-orange-500" />,
        color: 'bg-orange-50 border-orange-200',
        textColor: 'text-orange-800',
        title: 'DPE à vérifier',
        description: 'Des incohérences majeures nécessitent une vérification',
        action: 'Correction recommandée'
      };
    } else if (optimizationSuggestions > 0) {
      return {
        status: 'FIABLE',
        icon: <CheckCircle className="w-6 h-6 text-green-500" />,
        color: 'bg-green-50 border-green-200',
        textColor: 'text-green-800',
        title: 'DPE fiable avec opportunités d\'optimisation',
        description: `Le diagnostic est cohérent. ${optimizationSuggestions} piste${optimizationSuggestions > 1 ? 's' : ''} d'amélioration identifiée${optimizationSuggestions > 1 ? 's' : ''} pour optimiser votre consommation`,
        action: 'Optimisations recommandées'
      };
    } else {
      return {
        status: 'FIABLE',
        icon: <CheckCircle className="w-6 h-6 text-green-500" />,
        color: 'bg-green-50 border-green-200',
        textColor: 'text-green-800',
        title: 'DPE fiable',
        description: 'Le diagnostic correspond à la réalité terrain',
        action: 'Aucune action nécessaire'
      };
    }
  };

  const reliabilityStatus = getReliabilityStatus();
  
 

  // Handler pour naviguer vers les scénarios de travaux
  const handleNavigateToScenarios = () => {
    analytics.trackEvent({
      category: 'conversion',
      action: 'click_work_scenarios_header',
      label: dpeId,
      custom_parameters: {
        source: 'results_page_header'
      }
    });
    navigate(`/analysis/${dpeId}/scenarios`);
  };

  return (
    <div className="space-y-8">
      {/* Header with summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Résultats & recommandations</CardTitle>
              <CardDescription>
                Analyse effectuée le {formattedDate}
              </CardDescription>
            </div>

            <div className="flex items-center space-x-2">
              {!isLegacyDPE && (
                <Button
                  variant="primary"
                  size="sm"
                  className="no-print"
                  onClick={handleNavigateToScenarios}
                >
                  <Hammer className="w-4 h-4 mr-2" />
                  Simuler mes travaux
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                className="no-print"
                onClick={onModifyAnswers || (() => navigate(`/questions/${dpeId}`))}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Modifier mes réponses
              </Button>

              <PDFDownloadButton
                dpeData={sessionData?.dpe_data || {}}
                sessionData={sessionData}
                confidenceScore={resultsConfidenceScore}
                rulesAppliedCount={rulesApplied.length}
                inconsistenciesDetected={inconsistencies_detected}
                immediateActions={immediate_actions.filter(a => a.category !== 'DPE_CORRECTION' && a.category !== 'REGLEMENTAIRE')}
                shortTermActions={short_term_actions}
                hasCollectiveSystem={!!collectiveSystemWarning}
                collectiveSystemWarning={collectiveSystemWarning}
                analysisDate={analysisDate}
                variant="outline"
                size="sm"
                showAsTooltip={true}
                onDownloadStart={() => {
                  analytics.trackEvent({
                    category: 'analysis',
                    action: 'pdf_generation_start',
                    label: dpeId
                  });
                }}
                onDownloadComplete={() => {
                  analytics.trackEvent({
                    category: 'analysis',
                    action: 'export_pdf',
                    label: dpeId
                  });
                  onExportPDF?.();
                }}
                onDownloadError={(error) => {
                  analytics.trackEvent({
                    category: 'analysis',
                    action: 'pdf_generation_error',
                    label: dpeId,
                    custom_parameters: {
                      error_message: error.message
                    }
                  });
                }}
              />
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* ✅ RÉFORME JUILLET 2024: Note informative pour petites surfaces */}
          {(() => {
            const surfaceHabitable = parseFloat(sessionData?.dpe_data?.living_area) ||
                                    parseFloat(sessionData?.dpe_data?.surface_habitable_logement) ||
                                    null;

            if (surfaceHabitable && surfaceHabitable < 40) {
              return (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">
                        Seuils adaptatifs appliqués (Réforme juillet 2024)
                      </h4>
                      <p className="text-sm text-blue-800">
                        Votre logement ({surfaceHabitable.toFixed(0)}m²) bénéficie de seuils DPE ajustés pour corriger
                        le biais qui pénalisait les petites surfaces. Les besoins énergétiques incompressibles
                        (eau chaude sanitaire, cuisson) et le rapport surface/volume sont désormais pris en compte
                        pour une évaluation plus équitable.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Score de confiance */}
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg">
              <CircularProgress
                value={resultsConfidenceScore}
                size={120}
                strokeWidth={8}
                variant={
                  resultsConfidenceScore > 80 ? 'success' :
                  resultsConfidenceScore > 60 ? 'default' :
                  'warning'
                }
              />
              <h3 className="text-lg font-semibold text-gray-900 mt-4">
                Score de confiance
              </h3>
              <p className="text-sm text-gray-600 text-center mt-1">
                {rulesApplied.length} règles métier
              </p>
            </div>

            {/* Fiabilité et Recommandations combinées */}
            <div className="flex flex-col justify-between p-6 bg-gray-50 rounded-lg">
              {/* Fiabilité du DPE */}
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <Shield className="w-5 h-5 text-blue-500 mr-2" />
                  <h3 className="text-base font-semibold text-gray-900">
                    Fiabilité du DPE
                  </h3>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Points d'attention</span>
                    <span className="text-base font-semibold text-orange-600">
                      {inconsistencies_detected.filter(inc => inc.severity !== 'INFORMATION').length}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Pistes d'amélioration</span>
                    <span className="text-base font-semibold text-blue-600">
                      {inconsistencies_detected.filter(inc => inc.severity === 'INFORMATION').length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recommandations travaux */}
              <div>
                <div className="flex items-center mb-3">
                  <BarChart3 className="w-5 h-5 text-green-500 mr-2" />
                  <h3 className="text-base font-semibold text-gray-900">
                    Recommandations travaux
                  </h3>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Actions immédiates</span>
                    <span className="text-base font-semibold text-green-600">
                      {immediate_actions.filter(a => a.category !== 'DPE_CORRECTION' && a.category !== 'REGLEMENTAIRE').length}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Court terme</span>
                    <span className="text-base font-semibold text-green-600">
                      {short_term_actions.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Évolution coefficient électrique - Ancien système (masqué si nouveau système disponible) */}
      {showOldProjectionSystem && (
        <EvolutionCoefficientElectrique dpeProjection={dpeProjection} />
      )}

      {/* Warning système collectif */}
      {collectiveSystemWarning && (
        <Card className="border-2 border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-orange-900 font-medium">
                  {collectiveSystemWarning.has_chauffage_collectif && collectiveSystemWarning.has_ecs_collectif
                    ? 'Chauffage et eau chaude collectifs détectés.'
                    : collectiveSystemWarning.has_chauffage_collectif
                    ? 'Chauffage collectif détecté.'
                    : 'Eau chaude sanitaire collective détectée.'}
                  {' '}Les options de changement de ces systèmes ne sont pas proposées car vous ne pouvez pas agir individuellement sur des installations collectives.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerte DPE à vérifier - uniquement si incohérences MAJEURES ou CRITIQUES */}
      {realInconsistencies.length > 0 && (
        <Card className="border-2 border-orange-300 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-orange-900 mb-1">
                  DPE à vérifier — Incohérences majeures détectées
                </h4>
                <p className="text-sm text-orange-800">
                  Des écarts entre le DPE officiel et votre situation réelle nécessitent une correction auprès du diagnostiqueur.
                  La prise en compte de la VMC peut améliorer la performance énergétique.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section INCOHÉRENCES DÉTECTÉES */}
      {inconsistencies_detected.length > 0 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide mb-1">
              Incohérences détectées
            </h2>
            <p className="text-sm text-gray-600">
              {inconsistencies_detected.length} élément{inconsistencies_detected.length > 1 ? 's' : ''} du diagnostic présente{inconsistencies_detected.length > 1 ? 'nt' : ''} des différences avec votre situation réelle
            </p>
          </div>

          <div className="space-y-4">
            {/* Trier par sévérité: CRITIQUE > MAJEURE > INFORMATION */}
            {inconsistencies_detected
              .sort((a, b) => {
                const severityOrder = { CRITIQUE: 0, MAJEURE: 1, INFORMATION: 2 };
                return severityOrder[a.severity] - severityOrder[b.severity];
              })
              .map((inconsistency, index) => (
                <InconsistencyDetailCard
                  key={index}
                  title={getInconsistencyTitle(inconsistency.type)}
                  severity={inconsistency.severity}
                  dpeValue={inconsistency.suspected_error || inconsistency.dpe_value}
                  userValue={inconsistency.user_reality || inconsistency.user_value}
                  recommendation={inconsistency.recommendation}
                  impact={inconsistency.impact}
                />
              ))}
          </div>
        </div>
      )}

      {/* Section ACTIONS CORRECTIVES RECOMMANDÉES */}
      {uniqueDpeCorrection.length > 0 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide mb-1">
              Actions correctives recommandées
            </h2>
            <p className="text-sm text-gray-600">
              {uniqueDpeCorrection.length} action{uniqueDpeCorrection.length > 1 ? 's' : ''} à entreprendre pour corriger les incohérences du diagnostic
            </p>
          </div>

          <div className="space-y-4">
            {uniqueDpeCorrection.map((action) => (
              <CorrectiveActionCard
                key={action.id}
                title={action.title}
                description={action.description}
                category={action.category}
                priority={action.priority as 'CRITIQUE' | 'HAUTE' | 'MOYENNE' | 'BASSE'}
                difficulty={action.difficulty as 'FACILE' | 'MOYEN' | 'DIFFICILE'}
                estimatedCost={action.estimated_cost}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Section Travaux Recommandés - Nouveauté */}
      <RecommendedWorks
        dpeId={dpeId}
        immediate_actions={immediate_actions}
        short_term_actions={short_term_actions}
        isLegacyDPE={isLegacyDPE}
        surfaceHabitable={surfaceHabitable}
      />

      {/* Recommandations travaux */}
      {displayRecommendations.length > 0 && (
        <>
          {/* Simulateur d'impact des travaux - Ancien système (masqué si nouveau système disponible) */}
          {showOldProjectionSystem && (
            <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-green-50">
            <CardHeader>
              <div className="flex items-center">
                <Zap className="w-5 h-5 text-blue-500 mr-2" />
                <CardTitle>Simulateur d'impact des travaux</CardTitle>
              </div>
              <CardDescription>
                Cochez les travaux envisagés pour estimer l'impact sur votre DPE
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {/* Comparaison DPE actuel vs simulé */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* DPE Actuel */}
                <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                  <h4 className="font-medium text-gray-900 mb-3">DPE Actuel</h4>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-gray-800">
                      {Math.round(results.data?.consommation || 
                                 results.data?.primary_energy_consumption || 
                                 sessionData?.dpe_data?.conso_5_usages_par_m2_ep ||
                                 sessionData?.dpe_data?.primary_energy_consumption || 
                                 0)}
                    </div>
                    <div className="text-sm text-gray-600">kWh/m²/an</div>
                    <Badge energyClass={initialEnergyClass as any} size="lg" />
                  </div>
                </div>
                
                {/* DPE Simulé */}
                <div className={cn(
                  'rounded-lg p-4 border text-center transition-all duration-300',
                  hasClassImprovement 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-blue-50 border-blue-200'
                )}>
                  <h4 className={cn(
                    'font-medium mb-3',
                    hasClassImprovement ? 'text-green-800' : 'text-blue-800'
                  )}>
                    DPE Simulé {hasClassImprovement && '🎉'}
                  </h4>
                  <div className="space-y-2">
                    <div className={cn(
                      'text-2xl font-bold',
                      hasClassImprovement ? 'text-green-800' : 'text-blue-800'
                    )}>
                      {Math.round(simulatedConsumption)}
                    </div>
                    <div className={cn(
                      'text-sm',
                      hasClassImprovement ? 'text-green-600' : 'text-blue-600'
                    )}>
                      kWh/m²/an
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <Badge energyClass={simulatedEnergyClass as any} size="lg" />
                      {hasClassImprovement && (
                        <span className="text-green-600 font-bold text-sm">↗️</span>
                      )}
                    </div>
                  </div>
                  
                  {totalSelectedSavings > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-sm text-gray-600">
                        Économie totale: <span className="font-semibold text-green-600">
                          -{totalSelectedSavings} kWh/m²/an
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Liste des travaux avec cases à cocher */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Sélectionnez les travaux envisagés</h4>
                {workRecommendations.map(rec => (
                  <label 
                    key={rec.id} 
                    className={cn(
                      'flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200',
                      selectedWorkIds.has(rec.id)
                        ? 'border-blue-300 bg-blue-50 shadow-sm'
                        : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/50'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedWorkIds.has(rec.id)}
                      onChange={() => handleToggleWork(rec.id)}
                      className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-medium text-gray-900">{rec.title}</h5>
                        <div className="flex items-center space-x-2">
                          <Badge 
                            variant={
                              rec.priority === 'CRITIQUE' ? 'danger' :
                              rec.priority === 'HAUTE' ? 'warning' :
                              rec.priority === 'MOYENNE' ? 'info' : 'default'
                            }
                            size="sm"
                          >
                            Priorité {rec.priority}
                          </Badge>
                          <Badge variant="outline" size="sm">
                            Difficulté {rec.difficulty}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{rec.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-600 font-medium">
                          {rec.category === 'REGLEMENTAIRE'
                            ? `Économie: ${rec.estimated_savings || 0} kWh/m²/an (sans travaux, automatique en 2026)`
                            : (() => {
                                const euros = rec.estimated_savings && surfaceHabitable
                                  ? Math.round(rec.estimated_savings * surfaceHabitable * 0.13)
                                  : null;
                                return euros
                                  ? `≈ ${euros.toLocaleString('fr-FR')} €/an · ${rec.estimated_savings} kWh/m²/an`
                                  : `Économie : ${rec.estimated_savings || 0} kWh/m²/an`;
                              })()
                          }
                        </span>
                        <span className="text-xs text-gray-500">
                          {getCategoryLabel(rec.category)}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Résumé de la simulation */}
              {selectedWorkIds.size > 0 && (
                <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3">Résumé de votre simulation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-blue-600">{selectedWorkIds.size}</div>
                      <div className="text-sm text-gray-600">Travaux sélectionnés</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-green-600">
                        -{totalSelectedSavings}
                      </div>
                      <div className="text-sm text-gray-600">kWh/m²/an économisés</div>
                    </div>
                    {surfaceHabitable && totalSelectedSavings > 0 && (
                      <div>
                        <div className="text-lg font-bold text-green-600">
                          ≈ {Math.round(totalSelectedSavings * surfaceHabitable * 0.13).toLocaleString('fr-FR')} €/an
                        </div>
                        <div className="text-sm text-gray-600">Économies estimées</div>
                      </div>
                    )}
                    <div>
                      <div className={cn(
                        'text-lg font-bold',
                        hasClassImprovement ? 'text-green-600' : 'text-gray-600'
                      )}>
                        {hasClassImprovement
                          ? `${initialEnergyClass} → ${simulatedEnergyClass}`
                          : 'Pas d\'amélioration'
                        }
                      </div>
                      <div className="text-sm text-gray-600">Évolution classe DPE</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Disclaimer important */}
          {showOldProjectionSystem && (
            <>
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-2">⚠️ Simulation indicative uniquement</h4>
                      <div className="text-sm text-yellow-700 space-y-2">
                        <p>
                          <strong>Cette simulation est une estimation</strong> basée sur des économies moyennes et ne remplace en aucun cas un DPE officiel.
                        </p>
                        <p>
                          L'algorithme de calcul du DPE de l'ADEME (3CL-DPE) est complexe et prend en compte des interactions non linéaires entre les différents éléments du bâtiment. Les résultats de cette simulation sont fournis <strong>à titre indicatif</strong> et ne garantissent pas la classe DPE qui serait obtenue lors d'un diagnostic officiel.
                        </p>
                        <p className="font-medium">
                          💡 Utilisez cette simulation pour prioriser vos travaux, mais consultez un professionnel pour une estimation précise.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Link to SimulReno.com for work cost estimation */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <p className="text-gray-700 mb-4">
                  Pour obtenir un chiffrage précis de vos travaux, utilisez notre outil d'estimation détaillé :
                </p>
                <ExternalLinkTracker href="https://simulreno.com" trackingLabel="simulreno_link">
                  <Button variant="primary" size="lg">
                    <Zap className="w-4 h-4 mr-2" />
                    Accéder à SimulReno.com
                  </Button>
                </ExternalLinkTracker>
                <p className="text-xs text-gray-500 mt-2">
                  Outil externe spécialisé dans le chiffrage de travaux de rénovation énergétique
                </p>
              </div>
            </>
          )}
        </>
      )}
      
      {/* Détail de l'analyse - replié par défaut */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide mb-1">
            Détail de l'analyse
          </h2>
        </div>

        <RulesBreakdown
          rulesApplied={rulesApplied}
          factorsAnalysis={factorsAnalysis}
        />
      </div>

      {/* Appel à action simulation de travaux */}
      {!isLegacyDPE && <WorkScenariosCallToAction dpeId={dpeId} />}
    </div>
  );
};

// Fonctions utilitaires
const getInconsistencyTitle = (type: string) => {
  const titles = {
    'VOLETS_INCONSISTENCY': 'Protection solaire des menuiseries (volets, stores)',
    'VENTILATION_INCONSISTENCY': 'Système de ventilation',
    'VENTILATION_CLARIFICATION': 'Système de ventilation (précision)',
    'ISOLATION_INCONSISTENCY': 'Isolation thermique',
    'SURFACE_INCONSISTENCY': 'Surface habitable',
    'HAUTEUR_INCONSISTENCY': 'Hauteur sous plafond',
    'EQUIPEMENT_INCONSISTENCY': 'Équipements de chauffage',
    'MUR_ADJACENT_INCONSISTENCY': 'Isolation mur adjacent',
    'MENUISERIES_INCONSISTENCY': 'Qualité des menuiseries',
    'MENUISERIES_ETANCHEITE_CLARIFICATION': 'Étanchéité menuiseries (précision)',
    'ECS_SURDIMENSIONNEMENT': 'Dimensionnement ballon ECS',
    'ECS_OPTIMISATION_SURDIMENSIONNEMENT': 'Opportunité d\'optimisation ECS',
    'ECS_SOUS_DIMENSIONNEMENT': 'Dimensionnement ballon ECS',
    'ECS_MODE_FONCTIONNEMENT_CLARIFICATION': 'Mode fonctionnement ECS (précision)'
  };

  return titles[type] || 'Élément du diagnostic';
};

const getCategoryLabel = (category: string) => {
  const labels = {
    'ISOLATION': 'Isolation',
    'CHAUFFAGE': 'Chauffage',
    'VENTILATION': 'Ventilation',
    'MENUISERIES': 'Menuiseries',
    'DPE_CORRECTION': 'Correction DPE',
    'DPE_OPTIMIZATION': 'Optimisation DPE',
    'REGLEMENTAIRE': 'Réglementaire',
    'CONSEIL': 'Conseil',
    'AUDIT': 'Audit'
  };
  
  return labels[category] || category;
};

export default AnalysisResults;